import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

type OidcStoredUser = {
  access_token?: string;
  expires_at?: number;
};

const isValidTokenCandidate = (user: OidcStoredUser | null): user is OidcStoredUser => {
  if (!user?.access_token) return false;
  if (!user.expires_at) return true;
  // margem de 30s para evitar enviar token já expirando
  return user.expires_at > Math.floor(Date.now() / 1000) + 30;
};

const pickBestUserCandidate = (users: OidcStoredUser[]): OidcStoredUser | null => {
  const validUsers = users.filter(isValidTokenCandidate);
  if (validUsers.length === 0) return null;
  return validUsers.sort((a, b) => (b.expires_at || 0) - (a.expires_at || 0))[0];
};

// Helper function to retrieve the newest valid access token
const getAccessToken = (): string | null => {
  try {
    // Configurações do Cognito (mesmos valores do index.tsx/apiClient.ts)
    const authority = 'https://cognito-idp.us-east-1.amazonaws.com/us-east-1_sgMHVAasC';
    const clientId = '142lurdgkn78inlb9qup85bsa0';
    const storageKey = `oidc.user:${authority}:${clientId}`;

    const candidates: OidcStoredUser[] = [];
    const storages = [localStorage, sessionStorage];

    // Primeiro tenta a chave exata
    for (const storage of storages) {
      const userStr = storage.getItem(storageKey);
      if (!userStr) continue;
      try {
        const parsed = JSON.parse(userStr) as OidcStoredUser;
        candidates.push(parsed);
      } catch {
        // ignora entradas inválidas
      }
    }

    // Fallback genérico: procura por qualquer chave oidc.user
    for (const storage of storages) {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith('oidc.user:')) {
          const val = storage.getItem(key);
          if (!val) continue;
          try {
            const parsed = JSON.parse(val) as OidcStoredUser;
            candidates.push(parsed);
          } catch {
            // ignora entradas inválidas
          }
        }
      }
    }

    const bestCandidate = pickBestUserCandidate(candidates);
    return bestCandidate?.access_token || null;
  } catch (error) {
    console.error('Erro ao obter token do storage:', error);
  }
  return null;
};

class ApiClientService {
  private api: AxiosInstance;

  constructor(baseURL: string) {
    this.api = axios.create({
      baseURL
    });

    this.api.interceptors.request.use(
      async (config) => {
        const token = getAccessToken();

        if (token && config.headers && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Opcional: Redirecionar para login ou limpar storage
          // window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    config?: AxiosRequestConfig<D>
  ): Promise<R> {
    return this.api.get(url, config);
  }

  async post<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R> {
    return this.api.post(url, data, config);
  }

  async put<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R> {
    return this.api.put(url, data, config);
  }

  async delete<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    config?: AxiosRequestConfig<D>
  ): Promise<R> {
    return this.api.delete<T, R, D>(url, config);
  }
}

const API_URL = process.env.REACT_APP_API_URL!;

// Exporta a instância
export default new ApiClientService(API_URL);

export const DropdownHttpClient = new ApiClientService(API_URL);